import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { 
  Upload, 
  Download, 
  FileSpreadsheet,
  Search,
  Calendar,
  RefreshCw,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  X,
  Eye,
  Play,
  Volume2,
  Check,
  ChevronsUpDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { 
  getManualReports, 
  getManualReportsSummary,
  uploadManualReport, 
  downloadManualReportTemplate,
  deleteManualReport,
  getCampaigns
} from "../lib/api";
import { cn } from "../lib/utils";

function MetricCard({ label, value, icon: Icon, color = "blue" }) {
  const colorClasses = {
    blue: "text-[#3B82F6] bg-blue-50",
    green: "text-[#10B981] bg-emerald-50",
    amber: "text-[#F59E0B] bg-amber-50",
    purple: "text-[#8B5CF6] bg-purple-50"
  };
  
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function ManualReports() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Filters
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedUploadCampaign, setSelectedUploadCampaign] = useState("");
  
  // Campaign dropdown states
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [uploadDropdownOpen, setUploadDropdownOpen] = useState(false);
  
  const isSuperAdmin = user?.role === "super_admin";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page,
        page_size: pageSize
      };
      
      if (campaignFilter !== "all") {
        params.campaign_id = campaignFilter;
      }
      
      if (searchQuery) {
        params.campaign_name = searchQuery;
      }
      
      if (startDate) {
        params.start_date = startDate;
      }
      
      if (endDate) {
        params.end_date = endDate;
      }
      
      const [reportsRes, summaryRes, campaignsRes] = await Promise.all([
        getManualReports(params),
        getManualReportsSummary(params),
        getCampaigns()
      ]);
      
      setReports(reportsRes.data.reports || []);
      setTotalRecords(reportsRes.data.total || 0);
      setSummary(summaryRes.data);
      
      const campaignsData = campaignsRes.data?.campaigns || campaignsRes.data || [];
      // Filter to only show campaigns with run_mode === "manual"
      const manualCampaigns = (Array.isArray(campaignsData) ? campaignsData : [])
        .filter(c => c.run_mode === "manual");
      setCampaigns(manualCampaigns);
      
    } catch (error) {
      toast.error("Failed to load manual reports");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, campaignFilter, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTemplateDownload = async () => {
    try {
      await downloadManualReportTemplate();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    
    if (!selectedUploadCampaign) {
      toast.error("Please select a campaign first");
      return;
    }
    
    try {
      setUploading(true);
      const result = await uploadManualReport(file, selectedUploadCampaign);
      setUploadResult(result.data);
      setUploadDialogOpen(true);
      setShowUploadModal(false);
      setSelectedUploadCampaign("");
      
      if (result.data.success) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.message || "Failed to upload file");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await deleteManualReport(reportToDelete.id);
      toast.success("Report deleted successfully");
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatPercent = (num) => `${num?.toFixed(2) || '0'}%`;

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <div className="p-6" data-testid="manual-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Manual Reports</h1>
          <p className="text-sm text-slate-600 mt-1">
            Upload and manage performance reports for manual run mode campaigns
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleTemplateDownload}
            className="border-slate-200 text-slate-600 hover:text-slate-900"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          
          {isSuperAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => setShowUploadModal(true)}
                disabled={uploading || campaigns.length === 0}
                className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white"
                data-testid="upload-csv-btn"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSV
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <MetricCard
            label="Total Impressions"
            value={formatNumber(summary.total_impressions)}
            icon={Eye}
            color="blue"
          />
          <MetricCard
            label="Total Clicks"
            value={formatNumber(summary.total_clicks)}
            icon={Play}
            color="green"
          />
          <MetricCard
            label="Avg CTR"
            value={formatPercent(summary.avg_ctr)}
            icon={FileSpreadsheet}
            color="amber"
          />
          <MetricCard
            label="Total Reach"
            value={formatNumber(summary.total_reach)}
            icon={Eye}
            color="purple"
          />
          <MetricCard
            label="Completed Views"
            value={formatNumber(summary.total_completed_view)}
            icon={Play}
            color="green"
          />
          <MetricCard
            label="Completion Rate"
            value={formatPercent(summary.avg_completion_rate)}
            icon={Volume2}
            color="amber"
          />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600 font-medium">Filters:</span>
            </div>
            
            <div className="flex-1 min-w-[200px] max-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search campaign..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white border-slate-200"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Popover open={filterDropdownOpen} onOpenChange={setFilterDropdownOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={filterDropdownOpen}
                  className="w-[250px] justify-between bg-white border-slate-200"
                  data-testid="campaign-filter-dropdown"
                >
                  {campaignFilter === "all" 
                    ? "All Campaigns" 
                    : campaigns.find(c => c.id === campaignFilter)?.name || "Select campaign..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0 bg-white border-slate-200">
                <Command>
                  <CommandInput placeholder="Search campaigns..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>No campaign found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setCampaignFilter("all");
                          setFilterDropdownOpen(false);
                        }}
                      >
                        All Campaigns
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            campaignFilter === "all" ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                      {campaigns.map(c => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => {
                            setCampaignFilter(c.id);
                            setFilterDropdownOpen(false);
                          }}
                        >
                          {c.name}
                          <Check
                            className={cn(
                              "ml-auto h-4 w-4",
                              campaignFilter === c.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[140px] bg-white border-slate-200"
                placeholder="Start date"
                data-testid="start-date-input"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[140px] bg-white border-slate-200"
                placeholder="End date"
                data-testid="end-date-input"
              />
            </div>
            
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-slate-200"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-200 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-slate-900">
              Report Data
              <Badge variant="outline" className="ml-2 bg-slate-100">
                {totalRecords} records
              </Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <FileSpreadsheet className="w-12 h-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No manual reports found</p>
              <p className="text-sm mt-1">Upload a CSV file to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-600 font-semibold">Date</TableHead>
                    <TableHead className="text-slate-600 font-semibold">Campaign</TableHead>
                    <TableHead className="text-slate-600 font-semibold">Creative</TableHead>
                    <TableHead className="text-slate-600 font-semibold">Source</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">Impressions</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">Clicks</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">CTR</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">Reach</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">Completed</TableHead>
                    <TableHead className="text-slate-600 font-semibold text-right">Comp. Rate</TableHead>
                    {isSuperAdmin && (
                      <TableHead className="text-slate-600 font-semibold text-center w-[80px]">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow 
                      key={report.id} 
                      className="hover:bg-slate-50"
                      data-testid={`report-row-${report.id}`}
                    >
                      <TableCell className="text-slate-900 font-medium">{report.date}</TableCell>
                      <TableCell className="text-slate-900">{report.campaign_name}</TableCell>
                      <TableCell className="text-slate-600">{report.creative_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-100 text-slate-700">
                          {report.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-900">
                        {formatNumber(report.impressions)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#3B82F6]">
                        {formatNumber(report.clicks)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#10B981]">
                        {formatPercent(report.ctr)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-900">
                        {formatNumber(report.reach)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#8B5CF6]">
                        {formatNumber(report.completed_view)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#F59E0B]">
                        {formatPercent(report.completion_rate)}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setReportToDelete(report);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`delete-report-${report.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalRecords)} of {totalRecords} records
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-slate-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-slate-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Campaign Selection Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Upload Manual Report</DialogTitle>
            <DialogDescription className="text-slate-600">
              Select the campaign this report belongs to, then choose your CSV file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Select Campaign <span className="text-red-500">*</span>
              </label>
              <Popover open={uploadDropdownOpen} onOpenChange={setUploadDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={uploadDropdownOpen}
                    className="w-full justify-between bg-white border-slate-200"
                    data-testid="upload-campaign-dropdown"
                  >
                    {selectedUploadCampaign 
                      ? campaigns.find(c => c.id === selectedUploadCampaign)?.name 
                      : "Choose a campaign..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0 bg-white border-slate-200">
                  <Command>
                    <CommandInput placeholder="Search campaigns..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>No campaign found.</CommandEmpty>
                      <CommandGroup>
                        {campaigns.map(c => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setSelectedUploadCampaign(c.id);
                              setUploadDropdownOpen(false);
                            }}
                          >
                            {c.name}
                            <Check
                              className={cn(
                                "ml-auto h-4 w-4",
                                selectedUploadCampaign === c.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500">
                Only campaigns in &quot;Manual&quot; run mode are shown
              </p>
            </div>
            
            {selectedUploadCampaign && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  All rows in your CSV will be linked to: <strong>{campaigns.find(c => c.id === selectedUploadCampaign)?.name}</strong>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedUploadCampaign("");
              }}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={!selectedUploadCampaign || uploading}
              className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Select CSV File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Result Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {uploadResult?.success ? (
                <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
              ) : (
                <AlertCircle className="w-5 h-5 text-[#EF4444]" />
              )}
              Upload {uploadResult?.success ? "Complete" : "Failed"}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {uploadResult?.message}
            </DialogDescription>
          </DialogHeader>
          
          {uploadResult?.errors?.length > 0 && (
            <div className="max-h-[200px] overflow-y-auto">
              <p className="text-sm font-medium text-slate-700 mb-2">Errors:</p>
              <div className="space-y-1">
                {uploadResult.errors.map((error, idx) => (
                  <p key={idx} className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
                    {error}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => {
                setUploadDialogOpen(false);
                setUploadResult(null);
              }}
              className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Delete Report Entry</DialogTitle>
            <DialogDescription className="text-slate-600">
              Are you sure you want to delete this report entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {reportToDelete && (
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p><span className="text-slate-500">Campaign:</span> {reportToDelete.campaign_name}</p>
              <p><span className="text-slate-500">Date:</span> {reportToDelete.date}</p>
              <p><span className="text-slate-500">Source:</span> {reportToDelete.source}</p>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setReportToDelete(null);
              }}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
